import React, { useState, useEffect } from 'react';
import { useAuth } from "./AuthContext";
import { usePlanTracker, PLANS } from './hooks/usePlanTracker';
import './PricingPage.css';
import { Star, Check, ArrowRight, Loader2, Zap, AlertCircle } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

// ✅ REPLACE WITH YOUR ACTUAL STRIPE PAYMENT LINKS
const STRIPE_PAYMENT_LINKS = {
  PRO: 'https://buy.stripe.com/test_XXXXXXXXXXXXX',
  PREMIUM: 'https://buy.stripe.com/test_YYYYYYYYYYYYY'
};

// Convert PLANS object to array for rendering
const pricingPlans = [
  {
    ...PLANS.FREE,
    paymentLink: null
  },
  {
    ...PLANS.PRO,
    paymentLink: STRIPE_PAYMENT_LINKS.PRO
  },
  {
    ...PLANS.PREMIUM,
    paymentLink: STRIPE_PAYMENT_LINKS.PREMIUM
  }
];

const PricingPage = () => {
  const { user, authLoading } = useAuth();
  const planTracker = usePlanTracker(user?.uid);
  
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [searchParams] = useSearchParams();

  // Safely extract values with fallbacks
  const currentPlan = planTracker?.currentPlan || 'FREE';
  const planConfig = planTracker?.planConfig || PLANS.FREE;
  const planLoading = planTracker?.loading ?? true;
  const getUsageStats = planTracker?.getUsageStats;

  // Get usage stats safely
  const usageStats = user && getUsageStats ? getUsageStats() : null;

  console.log('PricingPage Debug:', {
    user: !!user,
    authLoading,
    planLoading,
    currentPlan,
    usageStats,
    planTracker
  });

  // ✅ Show success message after redirect from Stripe
  useEffect(() => {
    const success = searchParams.get('success');
    const plan = searchParams.get('plan');
    
    if (success === 'true' && plan) {
      setSuccessMessage(
        `🎉 Payment successful! Your ${plan.toUpperCase()} plan will be activated within 5 minutes. Please refresh if not updated.`
      );
      
      // Clear URL params after showing message
      setTimeout(() => {
        window.history.replaceState({}, '', window.location.pathname);
      }, 3000);
    }
  }, [searchParams]);

  // Handle plan selection with Payment Links
  const handlePlanSelect = (plan) => {
    setError(null);
    setSuccessMessage(null);

    // Handle free plan
    if (plan.name === "Free") {
      if (currentPlan === "FREE") {
        setError("You're already on the free plan!");
        return;
      }
      setError("To downgrade to the free plan, please cancel your subscription from Stripe Customer Portal.");
      return;
    }

    // Check if user is logged in
    if (!user) {
      setError("Please sign in to subscribe to a plan.");
      return;
    }

    // Check if user is already on this plan
    const planKey = plan.name.toUpperCase();
    if (currentPlan === planKey) {
      setError(`You're already subscribed to the ${plan.name} plan!`);
      return;
    }

    // ✅ Redirect to Stripe Payment Link
    if (plan.paymentLink) {
      const planName = plan.name.toLowerCase();
      
      // Store user info in localStorage for manual verification
      localStorage.setItem('pendingPayment', JSON.stringify({
        userId: user.uid,
        userEmail: user.email,
        plan: planName,
        timestamp: new Date().toISOString()
      }));

      // Redirect to Stripe Payment Link
      window.location.href = plan.paymentLink;
    } else {
      setError('Payment link not configured for this plan.');
    }
  };

  // Get current plan display name
  const getCurrentPlanDisplay = () => {
    if (!user || !currentPlan) return null;
    return PLANS[currentPlan]?.name || currentPlan;
  };

  const currentPlanDisplay = getCurrentPlanDisplay();

  // Show loading state ONLY if actually loading
  if (authLoading) {
    return (
      <section className="pricing-section" id="pricing">
        <div className="container">
          <div className="pricing-header">
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              padding: '4rem',
              flexDirection: 'column',
              gap: '1rem'
            }}>
              <Loader2 className="animate-spin" size={40} style={{ color: '#7c3aed' }} />
              <span style={{ color: '#a0a0a0' }}>Loading pricing plans...</span>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="pricing-section" id="pricing">
      <div className="container">
        <div className="pricing-header">
          <h2 className="section-title">Choose Your QR Code Plan</h2>
          <p className="section-subtitle">
            Start free and scale as your needs grow
          </p>
          
          {user && currentPlanDisplay && (
            <div className="current-plan-banner">
              <Zap size={20} />
              <span>
                Current plan: <strong>{currentPlanDisplay}</strong>
                {usageStats && typeof usageStats.qrRemaining === 'number' && (
                  <> • {usageStats.qrRemaining} QR codes remaining this month</>
                )}
                {usageStats && usageStats.qrRemaining === 'Unlimited' && (
                  <> • Unlimited QR codes</>
                )}
              </span>
            </div>
          )}

          {/* ✅ Success Message */}
          {successMessage && (
            <div className="success-banner" style={{
              backgroundColor: '#10b981',
              color: 'white',
              padding: '1rem',
              borderRadius: '8px',
              marginTop: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <Check size={20} />
              {successMessage}
            </div>
          )}
          
          {error && (
            <div className="error-banner">
              <AlertCircle size={20} />
              {error}
            </div>
          )}
        </div>
        
        <div className="pricing-grid">
          {pricingPlans.map((plan, index) => {
            const planKey = plan.name.toUpperCase();
            const isCurrentPlan = user && currentPlan === planKey;

            return (
              <div 
                key={index} 
                className={`pricing-card ${plan.accent} ${plan.popular ? 'popular' : ''} ${isCurrentPlan ? 'current-plan' : ''}`}
              >
                {plan.popular && (
                  <div className="popular-badge">
                    <Star className="badge-icon" />
                    Most Popular
                  </div>
                )}
                
                {isCurrentPlan && (
                  <div className="current-plan-badge">
                    <Check size={16} />
                    Current Plan
                  </div>
                )}
                
                <div className="plan-header">
                  <h3 className="plan-name">{plan.name}</h3>
                  <div className="plan-pricing">
                    <span className="plan-price">${plan.price}</span>
                    <span className="plan-period">{plan.period}</span>
                  </div>
                  <p className="plan-desc">{plan.description}</p>
                </div>
                
                <div className="plan-features">
                  {plan.features.map((feature, idx) => (
                    <div key={idx} className="feature-row">
                      <Check className="check-icon" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
                
                <button 
                  className={`plan-button ${plan.accent} ${isCurrentPlan ? 'current' : ''}`}
                  onClick={() => handlePlanSelect(plan)}
                  disabled={isCurrentPlan}
                >
                  {isCurrentPlan ? (
                    <>
                      <Check className="btn-icon" />
                      Current Plan
                    </>
                  ) : !user && plan.name !== "Free" ? (
                    <>
                      Sign In to Subscribe
                      <ArrowRight className="btn-icon" />
                    </>
                  ) : (
                    <>
                      {plan.cta}
                      <ArrowRight className="btn-icon" />
                    </>
                  )}
                </button>
              </div>
            );
          })}
        </div>

        {/* ✅ Updated Usage Stats Section with Dynamic QR tracking */}
        {user && usageStats && !planLoading && (
          <div className="usage-stats-section">
            <h3 className="usage-title">Your Usage This Month</h3>
            <div className="usage-grid">
              <div className="usage-card">
                <div className="usage-label">Total QR Codes Generated</div>
                <div className="usage-value">{usageStats.qrGenerated || 0}</div>
              </div>
              <div className="usage-card">
                <div className="usage-label">QR Codes Remaining</div>
                <div className="usage-value">
                  {typeof usageStats.qrRemaining === 'number' 
                    ? usageStats.qrRemaining 
                    : usageStats.qrRemaining || 'N/A'}
                </div>
              </div>
              
              {/* ✅ NEW: Dynamic QR Usage */}
              <div className="usage-card">
                <div className="usage-label">Dynamic QR Codes Used</div>
                <div className="usage-value">{usageStats.dynamicQRGenerated || 0}</div>
              </div>
              <div className="usage-card">
                <div className="usage-label">Dynamic QR Remaining</div>
                <div className="usage-value">
                  {typeof usageStats.dynamicQRRemaining === 'number' 
                    ? usageStats.dynamicQRRemaining 
                    : usageStats.dynamicQRRemaining || 'N/A'}
                </div>
              </div>
              
              {usageStats.hourlyUsage !== undefined && planConfig.limits.creditsPerHour && (
                <div className="usage-card">
                  <div className="usage-label">Hourly Usage</div>
                  <div className="usage-value">
                    {usageStats.hourlyUsage} / {planConfig.limits.creditsPerHour}
                  </div>
                </div>
              )}
              {usageStats.resetDate && (
                <div className="usage-card">
                  <div className="usage-label">Next Reset</div>
                  <div className="usage-value usage-date">
                    {new Date(usageStats.resetDate).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* FAQ Section */}
        <div className="faq-section">
          <h3 className="faq-title">Frequently Asked Questions</h3>
          <div className="faq-grid">
            <FAQItem 
              question="What's the difference between Static and Dynamic QR codes?"
              answer="Static QR codes contain fixed data that cannot be changed after creation. Dynamic QR codes allow you to edit the destination URL even after printing, and provide tracking analytics."
            />
            <FAQItem 
              question="Can I upgrade or downgrade my plan anytime?"
              answer="Yes! You can upgrade to a higher plan at any time. To downgrade, you'll need to cancel your current subscription through the Stripe Customer Portal, and you can then subscribe to a lower tier."
            />
            <FAQItem 
              question="What happens when I reach my monthly limit?"
              answer="Once you reach your monthly QR code limit, you'll need to either wait for the next billing cycle or upgrade to a higher plan. Your existing QR codes will continue to work."
            />
            <FAQItem 
              question="Do QR codes expire?"
              answer="No, your QR codes never expire. Once generated, they work forever. However, Dynamic QR codes require an active subscription to maintain editing and tracking features."
            />
            <FAQItem 
              question="What file formats can I download?"
              answer="Free plan: PNG and SVG. Pro plan: PNG, SVG, and PDF. Premium plan: All formats including high-resolution options."
            />
            <FAQItem 
              question="Can I cancel my subscription?"
              answer="Yes, you can cancel anytime through the Stripe Customer Portal. You'll retain access to your paid features until the end of your billing period."
            />
          </div>
        </div>

        {/* Manual Update Instructions */}
        <div className="pricing-footer">
          <p className="footer-note">
            All plans include access to our AI-powered QR code generator with customization options.
          </p>
          {user && (
            <p className="footer-note" style={{ fontSize: '0.875rem', color: '#888' }}>
              💡 After payment, your plan will be activated within 5 minutes. Contact support if not updated.
            </p>
          )}
        </div>
      </div>
    </section>
  );
};

// FAQ Item Component
const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className={`faq-item ${isOpen ? 'open' : ''}`}>
      <button 
        className="faq-question"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span>{question}</span>
        <svg 
          className="faq-icon"
          width="24" 
          height="24" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="currentColor" 
          strokeWidth="2"
        >
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </button>
      <div className="faq-answer">
        <p>{answer}</p>
      </div>
    </div>
  );
};

export default PricingPage;